import React, { useEffect, useState } from "react";
import { Row, Col, Button, InputGroup, Form } from "react-bootstrap";
import AssignDevices from "../../components/modals/assignDevices";
import {
  ToggleIconLeft,
  SearchIcon,
  PlusIconWhite,
} from "../../components/ui/icons";
import TreeView from "../../components/treeView";
import CreateEditGroupModal from "../../components/createEditGroupModal";
import useDeviceManagement from "./useDeviceManagement";
import useDeviceTable from "./useDeviceTable";
import TableView from "../../components/tableView";
import { DataAvailabilityMessages, RowType } from "../../common/constants";
import DeviceManagementColumns from "./deviceManagementColumns";
import { usePermissions } from "../../customHooks/usePermissions";
import "./deviceManagement.scss";
import NoActionPopup from "../../components/deleteModal/noActionPopup";
import EditDeviceModal from "../../components/updateDeviceModal/editDeviceModal";
import { useSelector } from "react-redux";
import { useSpinner } from "../../customHooks";
import { IInitialSliceState } from "../../common/types/commonTypes";
import { IAssignDevice, IDeviceRow } from "../../common/types/devicesTypes";

interface IUpdateDeviceResponse {
  updateDeviceApi: IInitialSliceState;
  unassignDeviceApi: IInitialSliceState;
}

const DeviceManagement = () => {
  const { hasPermissions } = usePermissions();
  const [tableData, setTableData] = React.useState([]);
  const [assignSuccessfully, setAssignSuccessfully] =
    React.useState<boolean>(false);
  const { deviceWithAllGroups, deviceWithGroups } = useDeviceTable();
  const updateDeviceResponse = useSelector(
    (state: IUpdateDeviceResponse) => state.updateDeviceApi
  );
  const unassignDeviceResponse = useSelector(
    (state: IUpdateDeviceResponse) => state.unassignDeviceApi
  );
  useSpinner(updateDeviceResponse.status);
  useSpinner(unassignDeviceResponse.status);

  const {
    isEmptyGroupShown,
    setIsEmptyGroupShown,
    handleSearchSubmit,
    searchTerm,
    handleSearchChange,
    groupsHierarchy,
    deviceArray,
    selectedRows,
    onRowSelectionChange,
    setSearchTerm,
    isSearching,
    setDeviceArray,
    setSelectedRows,
    showModal,
    showAssignModel,
    handleClose,
    handleOpen,
    shouldResetSelection,
    acknowledgeReset,
  } = useDeviceManagement();

  const columns = DeviceManagementColumns({ onRowSelectionChange, RowType });

  const [isClicked, setIsClicked] = useState(false);

  const handleButtonClick = () => {
    setIsClicked(!isClicked);
  };

  const addDeviceNameInDeviceArray = () => {
    const updatedSecondArray: IAssignDevice[] = deviceArray.map((device) => {
      const allSubRow = deviceWithAllGroups.flatMap((entry) => entry.subRows);
      const matchingSubRow = allSubRow.find(
        (subRow) => subRow.deviceId === device.deviceId
      );

      if (
        matchingSubRow &&
        matchingSubRow.deviceDisplayName !== device.deviceDisplayName
      ) {
        return {
          ...device,
          deviceDisplayName: matchingSubRow.deviceDisplayName,
        };
      }
      return device;
    });

    setDeviceArray(updatedSecondArray);
  };

  useEffect(() => {
    addDeviceNameInDeviceArray();
    if (deviceWithAllGroups && isEmptyGroupShown) {
      setTableData(deviceWithAllGroups);
    } else if (deviceWithGroups && !isEmptyGroupShown) {
      setTableData(deviceWithGroups);
    }
  }, [deviceWithAllGroups, deviceWithGroups]);

  const showCreateGroupButton = () => {
    return (
      <Button onClick={showModal}>
        <PlusIconWhite />
        <span>Create Group</span>
      </Button>
    );
  };

  return (
    <div className="device-mangement-container">
      <Col>
        <div className="device-management-heading">
          <div className="heading">
            <h1>Device Management</h1>
          </div>
          <div className="header-btn-group">
            <div className="dm-add-device-btn">
              {hasPermissions("user.edit") && (
                <>
                  <Button
                    variant="primary"
                    onClick={handleOpen}
                    disabled={!(deviceArray?.length > 0)}
                  >
                    Assign Devices to Group
                  </Button>
                  {deviceArray?.length > 0 && (
                    <AssignDevices
                      showModel={showAssignModel}
                      handleClose={handleClose}
                      devices={deviceArray}
                      setDeviceArray={setDeviceArray}
                      setSelectedRows={setSelectedRows}
                      setAssignSuccessfully={setAssignSuccessfully}
                    />
                  )}
                </>
              )}
            </div>
            <div className="dm-add-group-btn">
              {hasPermissions("user.edit") && showCreateGroupButton()}
            </div>
          </div>
        </div>
      </Col>
      <Col>
        <div className="dm-body">
          <Row>
            {hasPermissions("user.read") && (
              <Col sm={4}>
                <div className="dm-search-input">
                  <Form onSubmit={handleSearchSubmit}>
                    <InputGroup>
                      <Form.Control
                        placeholder="Search for devices (case sensitive)..."
                        aria-label="Search"
                        aria-describedby="basic-addon2"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <Button
                        id="search-tree-list"
                        onClick={handleSearchSubmit}
                      >
                        <SearchIcon />
                      </Button>
                    </InputGroup>
                  </Form>
                </div>
              </Col>
            )}

            <Col sm={8}>
              <NoActionPopup />
            </Col>
          </Row>
          <Row className="plr-12">
            <div className="device-management-checkboxtree-table-container">
              <div
                className={`device-managment-checkbox-tree ${
                  isClicked ? "clicked" : ""
                }`}
              >
                <div className="checkbox-tree-header">
                  <div className="checkboxtoggle">
                    <button onClick={handleButtonClick}>
                      <ToggleIconLeft />
                    </button>
                  </div>
                </div>
                <TreeView
                  nodes={groupsHierarchy}
                  setIsEmptyGroupShown={setIsEmptyGroupShown}
                  setSearchTerm={setSearchTerm}
                  isSearching={isSearching}
                />
              </div>
              <div className="device-managment-table">
                <TableView
                  data={tableData}
                  columns={columns}
                  noDataMessage={DataAvailabilityMessages.NO_DEVICE_AVAILABLE}
                  assignSuccessfully={assignSuccessfully}
                  setAssignSuccessfully={setAssignSuccessfully}
                  shouldResetSelection={shouldResetSelection}
                  acknowledgeReset={acknowledgeReset}
                />
              </div>
            </div>
          </Row>
        </div>
      </Col>
      <CreateEditGroupModal />
      <EditDeviceModal />
    </div>
  );
};

export default DeviceManagement;
